// Unpaid and partially paid invoices
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableContainer from "@material-ui/core/TableContainer";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import Container from "@material-ui/core/Container";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useMediaQuery } from "@material-ui/core";

import { fetchPartialAndNoPaidInvoicesAction } from "../../actions/invoiceActions";
import { getProductsByUser } from "../../actions/productActions";
import Spinner from "../Spinner/Spinner";
import NoData from "../svgIcons/NoData";

const inputStyle = {
  padding: "0.75rem 0.75rem",
  width: "50%",
  fontSize: "0.8rem",
  lineHeight: 1.25,
  color: "#55595c",
  backgroundColor: "#fff",
  backgroundImage: "none",
  backgroundClip: "padding-box",
  borderTop: "0",
  borderRight: "0",
  borderBottom: "1px solid #eee",
  borderLeft: "0",
  borderRadius: "3px",
  transition: "all 0.25s cubic-bezier(0.4, 0, 1, 1)",
};
const iconStyle = {
  fontSize: "1.5rem",
  marginRight: "8px",
  position: "absolute",
  right: "50%",
  top: 10,
};

const filterStyle = {
  position: "relative",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  flexWrap: "wrap",
  width: "100%",
};
const totalsStyle = {
  display: "flex",
  flexDirection: "column",
  color: "#23232E",
};

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const useStyles2 = makeStyles(() => ({
  table: {
    minWidth: 500,
  },
  tablecell: {
    fontSize: "16px",
  },
}));

const tableStyle = {
  width: 160,
  fontSize: 14,
  cursor: "pointer",
  borderBottom: "none",
  padding: "8px",
  textAlign: "center",
};
const headerStyle = { borderBottom: "none", textAlign: "center" };

function checkStatus(status) {
  return status === "Partial"
    ? {
        border: "solid 0px #1976d2",
        backgroundColor: "#baddff",
        padding: "8px 18px",
        borderRadius: "20px",
      }
    : status === "Unpaid"
    ? {
        border: "solid 0px red",
        backgroundColor: "#ffaa91",
        padding: "8px 18px",
        borderRadius: "20px",
      }
    : {
        border: "solid 0px green",
        backgroundColor: "#a5ffcd",
        padding: "8px 18px",
        borderRadius: "20px",
      };
}

const toCommas = (value) => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const InvoicesWithRest = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles2();
  const isMobile = useMediaQuery("(max-width: 600px)");

  if (isMobile) {
    inputStyle.width = "100%";
    iconStyle.right = 0;
  }

  const user = JSON.parse(localStorage.getItem("profile"));
  // Invoices are always owned by the admin account, assistants read their admin's data
  const ownerId =
    user?.result?.role === "assistant"
      ? user?.result?.userId?.[0]
      : user?.result?._id || user?.result?.googleId;

  const { invoices, totalCount, totals, isLoading } = useSelector(
    (state) => state.invoices
  );
  const { products } = useSelector((state) => state.product);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const itemId = selectedProduct?._id || "";

  // Debounce the search bar so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    // Invoices are only loaded once a product is picked
    if (!itemId) return;

    dispatch(
      fetchPartialAndNoPaidInvoicesAction(ownerId, {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        itemId,
      })
    );
    // eslint-disable-next-line
  }, [dispatch, page, rowsPerPage, debouncedSearch, itemId]);

  useEffect(() => {
    if (user) {
      dispatch(getProductsByUser({ search: ownerId, archived: false }));
    }
    // eslint-disable-next-line
  }, [dispatch]);

  if (!user) return null;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openInvoice = (id) => {
    history.push(`/invoice/${id}`);
  };

  const rows = invoices || [];
  const totalAmount = totals?.totalAmount || 0;
  const totalReceived = totals?.totalReceived || 0;
  const currency = rows[0]?.currency || "";

  return (
    <div>
      <Container
        style={{
          width: "85%",
          paddingTop: "70px",
          paddingBottom: "50px",
          border: "none",
        }}
      >
        <h2 style={{ color: "#23232E" }}>Unpaid & Partial Invoices</h2>

        <Container
          style={{
            width: "100%",
            paddingBottom: "20px",
            marginLeft: "-20px",
            border: "none",
          }}
        >
          <div style={filterStyle}>
            <input
              placeholder="Search with client name or email..."
              name="search"
              type="text"
              style={inputStyle}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <SearchRoundedIcon style={iconStyle} />

            <Autocomplete
              style={{ width: isMobile ? "100%" : 280, marginTop: "10px" }}
              options={products || []}
              getOptionLabel={(option) => option?.title || ""}
              getOptionSelected={(option, value) => option._id === value._id}
              value={selectedProduct}
              onChange={(event, newValue) => {
                setSelectedProduct(newValue);
                setPage(0);
              }}
              noOptionsText="No product found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product"
                  placeholder="Search a product..."
                />
              )}
            />
          </div>
        </Container>

        {!itemId ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              padding: "20px",
            }}
          >
            <NoData />
            <p style={{ padding: "40px", color: "gray", textAlign: "center" }}>
              Select a product to see its unpaid and partial invoices.
            </p>
          </div>
        ) : isLoading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              paddingTop: "20px",
            }}
          >
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              padding: "20px",
            }}
          >
            <NoData />
            <p style={{ padding: "40px", color: "gray", textAlign: "center" }}>
              No unpaid or partial invoice found.
            </p>
          </div>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table
                className={classes.table}
                aria-label="unpaid invoices table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={headerStyle}>Number</TableCell>
                    <TableCell style={headerStyle}>Client</TableCell>
                    <TableCell style={headerStyle}>Email</TableCell>
                    <TableCell style={headerStyle}>Amount</TableCell>
                    <TableCell style={headerStyle}>Paid</TableCell>
                    <TableCell style={headerStyle}>Rest</TableCell>
                    <TableCell style={headerStyle}>Due Date</TableCell>
                    <TableCell style={headerStyle}>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row) => {
                    const paid = row.totalAmountReceived || 0;
                    const rest = (row.total || 0) - paid;

                    return (
                      <TableRow
                        key={row._id}
                        style={{ cursor: "pointer" }}
                        onClick={() => openInvoice(row._id)}
                      >
                        <TableCell style={tableStyle}>
                          {row.invoiceNumber}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {row?.client?.name}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {row?.client?.email}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {row.currency} {toCommas((row.total || 0).toFixed(2))}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {row.currency} {toCommas(paid.toFixed(2))}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {row.currency} {toCommas(rest.toFixed(2))}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          {moment(row.dueDate).format("DD MMM YYYY")}
                        </TableCell>
                        <TableCell style={tableStyle}>
                          <button style={checkStatus(row.status)}>
                            {row.status}
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      colSpan={8}
                      count={totalCount || 0}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { "aria-label": "rows per page" },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            <Container
              style={{
                width: "100%",
                border: "none",
                display: "flex",
                justifyContent: "flex-end",
                padding: "1rem",
              }}
            >
              <div style={totalsStyle}>
                <h3>
                  Total Amount : {toCommas(totalAmount.toFixed(2))} {currency}
                </h3>
                <h3>
                  Total Amount Received : {toCommas(totalReceived.toFixed(2))}{" "}
                  {currency}
                </h3>
                <h3>
                  Total Rest : {toCommas((totalAmount - totalReceived).toFixed(2))}{" "}
                  {currency}
                </h3>
              </div>
            </Container>
          </>
        )}
      </Container>
    </div>
  );
};

export default InvoicesWithRest;
